import React from "react";
import { Button} from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCheckIcon, CopyPlusIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, } from "@/components/ui/tooltip";

interface CopyButtonProps {
  className?: string;
  textToCopy: string;
  buttonText?: string;
  tooltipText?: string;
  variant?: "default" | "neutral" | "flat" | "destructive" | "destructive-ghost" | "success" | "success-ghost" | "warning" | "warning-ghost" | "outline" | "secondary" | "ghost" | "link";
  tooltipDelay?: number;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  props?: any;
  scope?: boolean;
}

/**
 * @example
 * import { CopyButton } from "@/components/ui/copy-button"
 *
 * export function CampaignId({ id }: { id: string }) {
 *   return (
 *     <div className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
 *       {id}
 *       <CopyButton textToCopy={id} buttonText="" tooltipText="Copy campaign ID" variant="ghost" className="size-7 text-muted-foreground" />
 *     </div>
 *   )
 * }
 */
export function CopyButton({
  className: classNameProp,
  textToCopy,
  buttonText = "Copy",
  tooltipText = "Click to copy",
  variant = "ghost",
  tooltipDelay = 0,
  tooltipSide = "top",
  scope = true,
  props,
}: CopyButtonProps) {
  const className = cn("flex items-center gap-1", classNameProp);
  const [copied, setCopied] = React.useState(false);

  function copyToClipboard(e: React.MouseEvent, text: string) {
    setCopied(true);

    navigator.clipboard.writeText(text);
    e.preventDefault();

    setTimeout(() => {
      setCopied(false);
    }, 1500);
  }

  return (
    <Tooltip delayDuration={tooltipDelay}>
      <TooltipTrigger asChild>
        <Button
          size={buttonText === "" ? "icon" : "default"}
          variant={variant}
          className={cn("relative flex items-center shrink-0", className)}
          onClick={(e) => copyToClipboard(e, textToCopy)}
          scope={scope}
          {...props}
        >
          <span className="relative">
            <CheckCheckIcon
              className={`size-4 font-bold transition-transform duration-100 transform ${
                copied ? "rotate-0 scale-100" : "-rotate-90 scale-0"
              } absolute ${buttonText ? "mr-2" : ""}`}
            />
            <CopyPlusIcon
              className={`size-4 font-bold transition-transform duration-100 transform ${
                !copied ? "rotate-0 scale-100" : "rotate-90 scale-0"
              } ${buttonText ? "mr-2" : ""}`}
            />
          </span>
          {buttonText}
        </Button>
      </TooltipTrigger>
      {textToCopy && <TooltipContent side={tooltipSide}>{tooltipText}</TooltipContent>}
    </Tooltip>
  );
}
